import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Coffee } from 'lucide-react';
import { useAuthStore } from '../../store/authStore';
import { getShopByOwner, type Shop } from '../../services/shop-service';
import ShopQRCode from './ShopQRCode';
import ScanPassModal from './ScanPassModal';
import OwnerShell from './OwnerShell';
import CoralHeader from '../dashboard/CoralHeader';

// The counter script (O4): staff walks an app-averse regular through
// signup in about two minutes, ending with their old paper card traded
// into a wallet pass. One step visible at a time, big type, NEXT/BACK —
// this is read out loud to someone who has never installed an app before,
// so every screen says exactly what to say and what to tap.
//
// Pure composition, zero new API surface:
//   Step 1 reuses ShopQRCode — the same {appUrl}/?shop={owner_address}
//     link TillKit prints on the counter card.
//   Step 2 is copy only (nothing to click on this device — the button
//     it's describing lives in the customer's own dashboard, on their
//     phone). iOS copy mirrors InstallPrompt's existing "Share → Add to
//     Home Screen" instructions verbatim.
//   Step 3 embeds ScanPassModal (Task 7) preselected into trade-in mode
//     via the `initialMode` prop — same scanner the SCAN MEMBER PASS
//     button opens, just opened straight into TRADE-IN.

const STEPS = [1, 2, 3] as const;
type Step = (typeof STEPS)[number];

const STEP_LABELS: Record<Step, string> = {
  1: 'THEIR PHONE',
  2: 'ADD THE PASS',
  3: 'TRADE THEIR PAPER CARD',
};

export default function AssistedSignup() {
  const { userAddress } = useAuthStore();
  const navigate = useNavigate();
  const [shop, setShop] = useState<Shop | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<Step>(1);

  useEffect(() => {
    if (!userAddress) return;
    let cancelled = false;
    getShopByOwner(userAddress)
      .then((s) => { if (!cancelled) setShop(s); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [userAddress]);

  function back() {
    setStep((s) => (s > 1 ? ((s - 1) as Step) : s));
  }

  function next() {
    setStep((s) => (s < 3 ? ((s + 1) as Step) : s));
  }

  return (
    <OwnerShell
      active="more"
      header={<CoralHeader statusLabel="MORE" eyebrow="OWNER TOOLS" title="Assisted Signup" />}
    >
      <div className="space-y-3.5 pb-8">
        {/* sr-only: real landmark heading; CoralHeader's title already shows
            the page name visually. */}
        <h1 className="sr-only">Assisted Signup</h1>

        <button
          onClick={() => navigate('/shops/owner/dashboard')}
          className="font-grotesk font-bold text-[13px] text-body-grey hover:text-coral py-2 flex items-center gap-2"
        >
          <ArrowLeft size={13} /> Back to dashboard
        </button>

        <div className="bg-white rounded-[10px] p-[18px]">
          <p className="font-grotesk font-bold text-[17px] text-primary mb-[5px]">Sign up a regular</p>
          <p className="font-grotesk font-medium text-[13px] leading-[1.4] text-body-grey">
            About two minutes, start to finish. It&rsquo;s written for THEM — read it out loud.
          </p>
          <div className="flex gap-2 mt-4">
            {STEPS.map((s) => (
              <div
                key={s}
                className={`flex-1 text-center font-heading text-[8px] tracking-[1px] uppercase py-2 rounded-full border-2 ${
                  s === step ? 'border-coral text-coral' : 'border-hairline text-body-grey'
                }`}
              >
                STEP {s}
              </div>
            ))}
          </div>
        </div>

        {loading && <div className="bg-white rounded-[10px] h-64 animate-pulse" />}

        {!loading && !shop && (
          <div className="bg-white rounded-[10px] p-8 text-center">
            <Coffee size={40} className="mx-auto mb-4 text-coral" />
            <p className="font-heading text-[10px] text-body-grey mb-4">NO SHOP REGISTERED</p>
            <a
              href="/shops/register"
              className="inline-block rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-6"
            >
              Register my shop
            </a>
          </div>
        )}

        {!loading && shop && (
          <div className="bg-white rounded-[10px] p-[18px]">
            <h2 className="font-grotesk font-bold text-[17px] text-primary mb-4">
              STEP {step}: {STEP_LABELS[step]}
            </h2>

            {step === 1 && (
              <div>
                <p className="font-grotesk font-bold text-[15px] leading-relaxed text-primary mb-4">
                  THEY SCAN → SIGN IN WITH GOOGLE OR APPLE.
                  <br />
                  THAT&rsquo;S THE WHOLE INSTALL.
                </p>
                <ShopQRCode shopOwnerAddress={shop.owner_address} shopName={shop.name} />
              </div>
            )}

            {step === 2 && (
              <div>
                <p className="font-grotesk font-bold text-[15px] leading-relaxed text-primary mb-3">
                  {/* Quoted verbatim from AddToGoogleWallet.tsx's button label —
                      staff read this out loud, so it must match what the
                      customer sees: "▸ ADD CARD TO GOOGLE WALLET". */}
                  TELL THEM: TAP &ldquo;ADD CARD TO GOOGLE WALLET&rdquo; ON THEIR NEW SCREEN.
                </p>
                <p className="font-grotesk font-medium text-[13px] leading-[1.5] text-body-grey mb-4">
                  It&rsquo;s the button under their loyalty card, on their own phone. It updates itself
                  after every stamp — nothing else to install, nothing else to do.
                </p>
                <div className="rounded-[10px] border-2 border-hairline p-4">
                  <p className="font-heading text-[8px] tracking-[1px] uppercase text-coral mb-2">GOT AN IPHONE?</p>
                  <p className="font-grotesk font-medium text-[13px] leading-[1.5] text-body-grey">
                    {/* Quoted verbatim from AddToAppleWallet.tsx's button label —
                        staff read this out loud, so it must match what the
                        customer sees: "▸ ADD CARD TO APPLE WALLET". */}
                    SAME BUTTON, SAME SCREEN — tell them to tap &ldquo;ADD CARD TO APPLE WALLET&rdquo;
                    instead. It drops straight into Apple Wallet, no app install needed.
                  </p>
                  <p className="font-heading text-[8px] tracking-[1px] uppercase text-body-grey mt-3">
                    NO PROMPT? Fall back to SHARE <span aria-hidden>⎋</span> in Safari, then
                    &ldquo;ADD TO HOME SCREEN&rdquo;.
                  </p>
                </div>
              </div>
            )}

            {step === 3 && (
              <div>
                <p className="font-grotesk font-bold text-[15px] leading-relaxed text-primary mb-3">
                  SCAN THEIR NEW PASS. ENTER THE OLD CARD&rsquo;S STAMPS. HIT APPLY.
                </p>
                <p className="font-grotesk font-medium text-[13px] leading-[1.5] text-body-grey mb-4">
                  Watch the pass tick up — hand back nothing.
                </p>
                <ScanPassModal open initialMode="trade-in" onClose={back} />
              </div>
            )}

            <div className="flex justify-between mt-8">
              <button
                onClick={back}
                disabled={step === 1}
                className="rounded-full bg-white border-2 border-hairline text-body-grey font-grotesk font-bold text-[13px] py-3 px-5 disabled:opacity-30"
              >
                ← BACK
              </button>
              {step < 3 ? (
                <button
                  onClick={next}
                  className="rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-5"
                >
                  Next →
                </button>
              ) : (
                <button
                  onClick={() => navigate('/shops/owner/dashboard')}
                  className="rounded-full bg-coral text-white font-grotesk font-bold text-sm py-3 px-5"
                >
                  Done
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </OwnerShell>
  );
}
